import React, { useState, useEffect } from 'react';
import { todosAPI } from '../../services/api';
import LoadingScreen from '../LoadingScreen';
import styles from '../../styles/OnboardingScreen.module.css';

/**
 * Final onboarding screen that waits for async plan approval and todos creation
 *
 * Flow:
 * 1. Wait for approval promise to complete
 * 2. Then poll for todos to be created
 * 3. Enable "Start with my new plan!" button when both are ready
 */
function TodosLoadingScreen({ approvalPromise, onNext }) {
  const [approvalComplete, setApprovalComplete] = useState(false);
  const [todosReady, setTodosReady] = useState(false);
  const [newConversationId, setNewConversationId] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Time-based progress animation (always moves forward over 2 minutes, then jumps to completion)
  useEffect(() => {
    const startTime = Date.now();
    const totalDuration = 120000; // 2 minutes total

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;

      // Calculate time-based progress (0% → 95% over 2 minutes)
      const timeBasedProgress = Math.min((elapsed / totalDuration) * 95, 95);

      // If actually complete, jump to 100%
      if (todosReady) {
        setProgress(100);
      } else {
        // Otherwise use time-based progress
        setProgress(timeBasedProgress);
      }
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [todosReady]);

  // Step 1: Wait for approval promise
  useEffect(() => {
    if (!approvalPromise) {
      console.warn('[TodosLoading] No approval promise provided');
      setApprovalComplete(true); // Skip if no promise
      return;
    }

    console.log('[TodosLoading] 🚀 Waiting for plan approval to complete...');

    approvalPromise
      .then(response => {
        console.log('[TodosLoading] ✅ Plan approval completed');
        console.log('[TodosLoading] New conversation ID:', response.newConversationId);
        setNewConversationId(response.newConversationId);
        setApprovalComplete(true);
      })
      .catch(err => {
        console.error('[TodosLoading] ❌ Plan approval failed:', err);
        setError('Failed to approve plan. Please try again.');
      });
  }, [approvalPromise]);

  // Step 2: Once approval is complete, poll for todos
  useEffect(() => {
    if (!approvalComplete || todosReady) {
      return;
    }

    console.log('[TodosLoading] 🔍 Approval complete, now polling for todos...');

    const maxAttempts = 60; // 60 seconds max
    let currentAttempt = 0;

    const pollInterval = setInterval(async () => {
      currentAttempt++;

      try {
        // Check if todos exist for the new conversation
        const response = newConversationId
          ? await todosAPI.getTodos(newConversationId)
          : await todosAPI.getTodos();

        if (response && response.length > 0) {
          console.log('[TodosLoading] ✅ Todos found (', response.length, 'todos)');
          clearInterval(pollInterval);
          setTodosReady(true);
        } else if (currentAttempt % 5 === 0) {
          console.log('[TodosLoading] ⏳ Still waiting for todos... (attempt', currentAttempt, '/', maxAttempts, ')');
        }

        if (currentAttempt >= maxAttempts) {
          console.error('[TodosLoading] ❌ Timeout waiting for todos after', maxAttempts, 'seconds');
          clearInterval(pollInterval);
          setError('Todos creation is taking longer than expected.');
        }
      } catch (err) {
        console.error('[TodosLoading] ❌ Error checking for todos:', err);
        if (currentAttempt >= maxAttempts) {
          clearInterval(pollInterval);
          setError('Error checking for todos.');
        }
      }
    }, 1000); // Check every second

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
    };
  }, [approvalComplete, todosReady, newConversationId]);

  const handleRetry = () => {
    setError(null);
    setApprovalComplete(false);
    setTodosReady(false);
  };

  const handleStart = () => {
    console.log('[TodosLoading] 🎉 User clicked "Start with my new plan!"');
    onNext();
  };

  // Construct image URL based on environment
  const getImageUrl = (filename) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      return `${apiUrl}/onboarding/${filename}`;
    }
    return `/onboarding/${filename}`;
  };

  if (error) {
    return (
      <div className={styles.todosLoadingContainer}>
        <div className={styles.planApprovalError}>
          <h2>Setup Taking Longer Than Expected</h2>
          <p>{error}</p>
          <button
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show final screen with button
  return (
    <div className={styles.todosLoadingContainer}>
      {/* Hero image header */}
      <div className={styles.welcomeHeader}>
        <img
          src={getImageUrl('hero-setting-up.png')}
          alt="Setting up your plan"
          className={styles.welcomeHeroImage}
        />
      </div>

      <div className={styles.todosLoadingContent}>
        <h2 className={styles.title}>We're finishing setting all up for you</h2>

        {!approvalComplete && (
          <div className={styles.todosLoadingStatus}>
            <LoadingScreen
              text="Approving your plan..."
              variant="progress"
              progress={progress}
              fullScreen={false}
            />
          </div>
        )}

        {approvalComplete && !todosReady && (
          <div className={styles.todosLoadingStatus}>
            <LoadingScreen
              text="Creating your action items..."
              variant="progress"
              progress={progress}
              fullScreen={false}
            />
          </div>
        )}

        {approvalComplete && todosReady && (
          <div className={styles.todosLoadingSuccess}>
            <p className={styles.successMessage}>✅ Your plan is ready!</p>
          </div>
        )}

        <button
          className={`${styles.button} ${styles.buttonPrimary} ${styles.todosLoadingButton}`}
          onClick={handleStart}
          disabled={!todosReady}
          style={{
            opacity: todosReady ? 1 : 0.5,
            cursor: todosReady ? 'pointer' : 'not-allowed'
          }}
        >
          Start with my new plan!
        </button>

        {!todosReady && (
          <p className={styles.todosLoadingSubtext}>
            This can take up to 2 minutes - Don't close the app
          </p>
        )}
      </div>
    </div>
  );
}

export default TodosLoadingScreen;
